import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { createContext, useContext, useId, useMemo } from 'react';
import { Form as AriaForm } from 'react-aria-components';
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerReturn,
  UseFormReturn,
} from 'react-hook-form';
import { FormProvider, useController, useFormContext } from 'react-hook-form';

interface FormProps<
  TFieldValues extends FieldValues = FieldValues,
> extends ComponentPropsWithoutRef<typeof AriaForm> {
  form: UseFormReturn<TFieldValues>;
  children: ReactNode;
}

interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  control: Control<TFieldValues>;
  children: ReactNode | ((control: UseControllerReturn<TFieldValues, TName>) => ReactNode);
}

interface FormFieldContextValues<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  id: string;
  name: TName;
  control?: UseControllerReturn<TFieldValues, TName>;
}

// Upstream fix: the registry copy defaults this context to `{}`, which makes
// the missing-provider guard below unreachable and lets `getFieldState` run
// with an undefined name. A null default restores the guard.
const FormFieldContext = createContext<FormFieldContextValues | null>(null);

export const useFormFieldContext = () => {
  const context = useContext(FormFieldContext);

  if (!context) {
    throw new Error("The 'useFormFieldContext' hook must be used within a '<FormField />'");
  }

  const { getFieldState, formState } = useFormContext();
  const fieldState = getFieldState(context.name, formState);

  return { ...context, ...fieldState };
};

export const HookForm = <TFieldValues extends FieldValues = FieldValues>({
  form,
  ...props
}: FormProps<TFieldValues>) => {
  return (
    <FormProvider {...form}>
      <AriaForm {...props} />
    </FormProvider>
  );
};

HookForm.displayName = 'HookForm';

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  ...props
}: FormFieldProps<TFieldValues, TName>) => {
  const id = 'form-item-' + useId();
  const control = useController(props);
  const withValidationBehavior = {
    ...control,
    field: {
      ...control.field,
      validationBehavior: 'aria',
    },
  };

  const contextValue = useMemo(
    () => ({
      id,
      name: props.name as FieldPath<FieldValues>,
      control: control as UseControllerReturn<FieldValues>,
    }),
    [id, props.name, control],
  );

  return (
    <FormFieldContext.Provider value={contextValue}>
      {children && (typeof children === 'function' ? children(withValidationBehavior) : children)}
    </FormFieldContext.Provider>
  );
};

FormField.displayName = 'FormField';
